// Word/character-level inline diff for a single changed line (old vs new),
// producing display spans. Used for in-place modifications and the edited lines
// inside a moved block.
//
// Modes:
//   'word' — token-level (default granularity)
//   'char' — character-level
//   'auto' — word-level, then tighten single-token replacements by stripping the
//            common prefix/suffix so `toISOString` → `toLocaleString` highlights
//            just `ISO`→`Locale`, not the whole token.
import { diffWordsWithSpace, diffChars } from 'diff';

/** @returns {Array<{text:string, type:'ctx'|'old'|'new'}>} */
export function inlineDiff(oldText, newText, mode = 'auto') {
  if (mode === 'char') return toSpans(diffChars(oldText, newText));
  const spans = toSpans(diffWordsWithSpace(oldText, newText));
  return mode === 'word' ? spans : refine(spans);
}

function toSpans(parts) {
  return parts.map((p) => ({ text: p.value, type: p.added ? 'new' : p.removed ? 'old' : 'ctx' }));
}

// Tighten adjacent old→new replacements of a single token by pulling the shared
// leading/trailing characters out as context.
function refine(spans) {
  const out = [];
  for (let i = 0; i < spans.length; i++) {
    const cur = spans[i];
    const next = spans[i + 1];
    if (cur.type === 'old' && next && next.type === 'new' && !hasSpace(cur.text) && !hasSpace(next.text)) {
      const o = cur.text;
      const n = next.text;
      let p = 0;
      while (p < o.length && p < n.length && o[p] === n[p]) p++;
      let s = 0;
      while (s < o.length - p && s < n.length - p && o[o.length - 1 - s] === n[n.length - 1 - s]) s++;
      const prefix = o.slice(0, p);
      const suffix = s ? o.slice(o.length - s) : '';
      const oldCore = o.slice(p, o.length - s);
      const newCore = n.slice(p, n.length - s);
      if (prefix) out.push({ text: prefix, type: 'ctx' });
      if (oldCore) out.push({ text: oldCore, type: 'old' });
      if (newCore) out.push({ text: newCore, type: 'new' });
      if (suffix) out.push({ text: suffix, type: 'ctx' });
      i++; // consumed `next`
    } else {
      out.push(cur);
    }
  }
  return out;
}

const hasSpace = (s) => /\s/.test(s);
